import { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import api from "../lib/api";
import { fmtDate } from "../lib/date";
import { Button } from "./ui/button";
import { toast } from "sonner";
import { ShieldCheck, ShieldWarning, FileText } from "@phosphor-icons/react";

const REQ_OPTIONS = [["inherit", "Inherit from asset type"], ["yes", "Yes — insurance required"], ["no", "No — not required"]];

const PILL = {
  valid: "bg-emerald-100 text-emerald-900 border-emerald-500",
  expiring: "bg-amber-100 text-amber-900 border-amber-500",
  invalid: "bg-red-600 text-white border-red-800",
  slate: "bg-slate-100 text-slate-600 border-slate-300",
};

// Compact, symmetric insurance dimension for the Compliance box.
// Everyone sees the status badge (+ expiry). Only admins see the full policy
// details, the certificate and the edit controls.
export default function AssetInsurancePanel({ asset, isAdmin, onSaved }) {
  const [st, setSt] = useState(null);
  const [types, setTypes] = useState([]);
  const [edit, setEdit] = useState(false);
  const [f, setF] = useState({ insurance_required: asset.insurance_required || "inherit", insurance_policy_type_id: asset.insurance_policy_type_id || "" });

  const load = useCallback(() => {
    api.get(`/insurance/asset/${asset.id}`).then((r) => setSt(r.data)).catch(() => setSt(null));
  }, [asset.id]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => { if (isAdmin) api.get("/config/insurance-policy-types").then((r) => setTypes(r.data)).catch(() => {}); }, [isAdmin]);

  const openDoc = async (fid) => {
    try { const r = await api.get(`/files/${fid}/url`); window.open(r.data.url, "_blank"); }
    catch (e) { toast.error("Could not open certificate"); }
  };
  const save = async () => {
    try {
      await api.put(`/assets/${asset.id}`, { insurance_required: f.insurance_required, insurance_policy_type_id: f.insurance_policy_type_id || null });
      toast.success("Insurance requirement updated"); setEdit(false); load(); onSaved && onSaved();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  if (!st) return <p className="text-slate-400 text-sm p-2">Loading insurance…</p>;

  const notReq = st.state === "not_required";
  const valid = st.state === "valid";
  const expiring = valid && st.policy?.state === "expiring";
  const tone = notReq ? "slate" : expiring ? "expiring" : valid ? "valid" : "invalid";
  const label = notReq ? "Not required" : valid ? "Valid" : st.state === "expired" ? "Expired" : "Not valid";
  const Icon = valid ? ShieldCheck : ShieldWarning;

  return (
    <div data-testid="compliance-insurance-panel">
      <div className="flex items-center gap-2 mb-1 flex-wrap">
        <Icon size={18} weight="bold" className="text-slate-500 shrink-0" />
        <span className="font-body font-semibold">Insurance</span>
        <span data-testid={`insurance-badge-${tone}`} className={`inline-flex items-center gap-1.5 px-2.5 py-1 border-2 text-xs font-bold uppercase tracking-wide rounded ${PILL[tone]}`}>
          <Icon size={14} weight="bold" />Insurance: {label}
        </span>
      </div>

      {notReq ? (
        <div className="font-body text-sm text-slate-400">Not required</div>
      ) : valid ? (
        <div className="font-mono text-xs text-slate-600 space-y-0.5">
          <div>Expires: {fmtDate(st.policy?.expiry) || "—"}{expiring ? " · soon" : ""}</div>
          {isAdmin && st.policy?.name && <div>{st.policy.name}</div>}
          {isAdmin && st.policy?.policy_number && <div>Policy: {st.policy.policy_number}</div>}
        </div>
      ) : (
        <div className="font-body text-sm text-red-700 font-semibold">
          {st.state === "expired" ? "Policy expired" : "No active cover"}
        </div>
      )}

      {isAdmin && valid && st.policy?.document_file_id && (
        <Button data-testid="asset-insurance-view-cert" onClick={() => openDoc(st.policy.document_file_id)} className="h-8 mt-2 bg-emerald-600 hover:bg-emerald-700 font-semibold text-xs"><FileText size={13} weight="bold" className="mr-1" />Certificate</Button>
      )}

      {isAdmin && (
        <div className="mt-2 flex flex-col gap-1">
          <button data-testid="asset-insurance-edit" onClick={() => setEdit((v) => !v)} className="text-xs text-blue-600 font-semibold text-left">{edit ? "Cancel" : "Edit insurance requirement"}</button>
          {edit && (
            <div className="bg-white border-2 border-slate-200 rounded-lg p-3 flex flex-col gap-2" data-testid="asset-insurance-req-editor">
              <label className="text-xs font-semibold text-slate-600">Insurance required</label>
              <select data-testid="asset-insurance-required" className="h-10 border-2 border-slate-300 rounded px-2 text-sm" value={f.insurance_required} onChange={(e) => setF({ ...f, insurance_required: e.target.value })}>
                {REQ_OPTIONS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
              </select>
              <label className="text-xs font-semibold text-slate-600">Required policy type (optional)</label>
              <select data-testid="asset-insurance-req-type" className="h-10 border-2 border-slate-300 rounded px-2 text-sm" value={f.insurance_policy_type_id} onChange={(e) => setF({ ...f, insurance_policy_type_id: e.target.value })}>
                <option value="">Any policy type</option>
                {types.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
              </select>
              <Button data-testid="asset-insurance-req-save" onClick={save} className="h-9 bg-blue-600 hover:bg-blue-700 font-semibold text-sm">Save requirement</Button>
            </div>
          )}
          <Link to="/insurance" data-testid="asset-insurance-manage-link" className="text-xs text-blue-600 font-semibold">Manage company insurance →</Link>
        </div>
      )}
    </div>
  );
}
